import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { Card, CardContent } from '@/components/ui/card';
import { ShoppingBag, Receipt, Package, BarChart3, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HomeCardProps {
  title: string;
  icon: React.ReactNode;
  path: string;
}

function HomeCard({ title, icon, path }: HomeCardProps) {
  const [, setLocation] = useLocation();

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-300",
        "hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]",
        "border border-blue-200 bg-white",
        "hover:border-blue-400 hover:bg-blue-50/50"
      )}
      onClick={() => setLocation(path)}
    >
      <CardContent className="p-5 sm:p-6 flex flex-col items-center justify-center gap-4 min-h-[130px] sm:min-h-[150px]">
        <div className="text-blue-600 transition-transform duration-300 group-hover:scale-110">
          {icon}
        </div>
        <div className="flex items-center justify-between w-full">
          <span className="text-sm sm:text-base font-semibold text-slate-800">{title}</span>
          <ChevronRight className="h-4 w-4 text-blue-500" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function HomePage() {
  const { t } = useTranslation('dashboard');
  const menuItems: HomeCardProps[] = [
    {
      title: t('homePage.sale'),
      icon: <ShoppingBag className="h-8 w-8 sm:h-10 sm:w-10" />,
      path: '/sale',
    },
    {
      title: t('homePage.order'),
      icon: <Receipt className="h-8 w-8 sm:h-10 sm:w-10" />,
      path: '/orders',
    },
    {
      title: t('homePage.product'),
      icon: <Package className="h-8 w-8 sm:h-10 sm:w-10" />,
      path: '/products',
    },
    {
      title: t('homePage.report'),
      icon: <BarChart3 className="h-8 w-8 sm:h-10 sm:w-10" />,
      path: '/reports',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50/50">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
          {menuItems.map((item) => (
            <HomeCard key={item.path} {...item} />
          ))}
        </div>
      </div>
    </div>
  );
}
